import { fetchEventSource } from '@microsoft/fetch-event-source';

import type { ApiClient } from './client.js';

export interface ChatHistoryItem {
  readonly role: 'user' | 'assistant';
  readonly content: string;
}

export interface ChatContextHit {
  readonly id: string;
  readonly name: string;
  readonly filePath: string;
  readonly snippet: string;
}

export interface StreamHandlers {
  onContext(hits: ChatContextHit[]): void;
  onToken(delta: string): void;
  onDone(finishReason: string): void;
  onError(message: string): void;
}

interface ContextEvent {
  readonly type: 'context';
  readonly hits: ChatContextHit[];
}
interface TokenEvent {
  readonly type: 'token';
  readonly delta: string;
}
interface UsageEvent {
  readonly type: 'usage';
  readonly usage: { readonly promptTokens: number; readonly completionTokens: number };
}
interface DoneEvent {
  readonly type: 'done';
  readonly finishReason: string;
}
interface ErrorEvent {
  readonly type: 'error';
  readonly message: string;
}
type ServerEvent = ContextEvent | TokenEvent | UsageEvent | DoneEvent | ErrorEvent;

/**
 * Streams a chat reply via Server-Sent Events. Returns a `cancel()` function
 * that aborts the underlying request. The Bearer token is fetched lazily on
 * each (re)connect via `api.authHeader()` so MSAL refresh is transparent.
 */
export function streamChat(
  api: ApiClient,
  slug: string,
  message: string,
  history: readonly ChatHistoryItem[],
  handlers: StreamHandlers,
  signal?: AbortSignal,
): () => void {
  const controller = new AbortController();
  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener('abort', () => controller.abort(), { once: true });
  }
  const url = `${api.baseUrl}/api/repos/${encodeURIComponent(slug)}/chat`;

  void fetchEventSource(url, {
    method: 'POST',
    signal: controller.signal,
    openWhenHidden: true,
    fetch: async (input, init): Promise<Response> => {
      const auth = await api.authHeader();
      const headers = new Headers(init?.headers);
      for (const [k, v] of Object.entries(auth)) headers.set(k, v);
      headers.set('Accept', 'text/event-stream');
      headers.set('Content-Type', 'application/json');
      return fetch(input, {
        ...init,
        headers,
        body: JSON.stringify({ message, history }),
      });
    },
    onmessage: (msg) => {
      if (msg.data === '[DONE]') {
        controller.abort();
        return;
      }
      dispatch(msg.data, handlers);
    },
    onerror: (err) => {
      handlers.onError(err instanceof Error ? err.message : String(err));
      throw err;
    },
  }).catch((err: unknown) => {
    if (controller.signal.aborted) return;
    handlers.onError(err instanceof Error ? err.message : String(err));
  });

  return () => controller.abort();
}

function dispatch(data: string, handlers: StreamHandlers): void {
  let parsed: ServerEvent;
  try {
    parsed = JSON.parse(data) as ServerEvent;
  } catch {
    return;
  }
  if (parsed.type === 'context') handlers.onContext(parsed.hits);
  else if (parsed.type === 'token') handlers.onToken(parsed.delta);
  else if (parsed.type === 'done') handlers.onDone(parsed.finishReason);
  else if (parsed.type === 'error') handlers.onError(parsed.message);
}
